import React, { useState, useEffect } from 'react';
import { Zap, Plus, TrendingUp } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { toast } from 'sonner';
import api from '../utils/api';

const ActionsBalance = () => {
  const [actions, setActions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [buyDialogOpen, setBuyDialogOpen] = useState(false);
  const [purchasing, setPurchasing] = useState(false);

  useEffect(() => {
    fetchActionsBalance();
    
    // Poll every 30 seconds to keep balance updated
    const interval = setInterval(fetchActionsBalance, 30000);
    
    // Listen for custom event to open buy dialog
    const handleOpenDialog = () => setBuyDialogOpen(true);
    window.addEventListener('open-buy-actions-dialog', handleOpenDialog);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('open-buy-actions-dialog', handleOpenDialog);
    };
  }, []);

  const fetchActionsBalance = async () => {
    try {
      const response = await api.get('/actions/balance');
      setActions(response.data);
    } catch (error) {
      console.error('Error fetching actions balance:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBuyActions = async () => {
    try {
      setPurchasing(true);
      const response = await api.post('/actions/purchase');
      
      // Redirect to Stripe checkout
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      console.error('Error purchasing actions:', error);
      toast.error('Failed to initiate purchase. Please try again.');
      setPurchasing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 rounded-lg animate-pulse">
        <Zap className="w-4 h-4 text-gray-400" />
        <span className="text-sm text-gray-400">...</span>
      </div>
    );
  }

  if (!actions) return null;

  // Determine color based on balance
  const getBalanceColor = () => {
    if (actions.is_unlimited) return 'text-purple-600 bg-purple-50 border-purple-200';
    if (actions.actions_balance <= 10) return 'text-red-600 bg-red-50 border-red-200';
    if (actions.actions_balance <= 30) return 'text-amber-600 bg-amber-50 border-amber-200';
    return 'text-emerald-600 bg-emerald-50 border-emerald-200';
  };

  const getIconColor = () => {
    if (actions.is_unlimited) return 'text-purple-600';
    if (actions.actions_balance <= 10) return 'text-red-600';
    if (actions.actions_balance <= 30) return 'text-amber-600';
    return 'text-emerald-600';
  };

  return (
    <>
      {/* Actions Icon - matches notification/guide style */}
      <button
        onClick={() => setBuyDialogOpen(true)}
        className="relative p-2 rounded-lg hover:bg-gray-100 transition-colors"
        title={`${actions.is_unlimited ? 'Unlimited' : actions.actions_balance} Actions available`}
      >
        <Zap className="w-5 h-5 text-gray-600" />
        {/* Badge with number */}
        <span 
          className={`absolute -top-1 -right-1 min-w-[18px] h-[18px] flex items-center justify-center text-xs font-bold rounded-full ${
            actions.is_unlimited ? 'bg-purple-500 text-white' :
            actions.actions_balance <= 10 ? 'bg-red-500 text-white' :
            actions.actions_balance <= 30 ? 'bg-amber-500 text-white' :
            'bg-emerald-500 text-white'
          }`}
          style={{ padding: '0 4px' }}
        >
          {actions.is_unlimited ? '∞' : actions.actions_balance}
        </span>
      </button>

      {/* Buy Actions Dialog */}
      <Dialog open={buyDialogOpen} onOpenChange={setBuyDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Top Up Automation Actions</DialogTitle>
            <DialogDescription>
              Purchase additional actions to continue automating your charter operations
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            {/* Current Balance */}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Current Balance</p>
                  <p className="text-2xl font-bold text-gray-900">{actions.actions_balance} Actions</p>
                </div>
                <Zap className="w-8 h-8 text-emerald-500" fill="currentColor" />
              </div>
              <div className="mt-2 text-xs text-gray-500">
                {actions.monthly_actions > 0 && (
                  <span>Monthly: {actions.monthly_actions} • </span>
                )}
                Purchased: {actions.purchased_actions}
              </div>
            </div>

            {/* Purchase Option */}
            <div className="border-2 border-blue-200 rounded-lg p-4 bg-blue-50">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <h3 className="font-semibold text-lg text-gray-900">Action Booster Pack</h3>
                  <p className="text-sm text-gray-600">50 Automation Actions</p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-blue-600">$0.50</p>
                  <p className="text-xs text-gray-500">~1¢ per action</p>
                </div>
              </div>
              <ul className="space-y-1 text-sm text-gray-600 mb-3">
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full"></span>
                  Never expires
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full"></span>
                  Use anytime
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full"></span>
                  One-time purchase
                </li>
              </ul>
              <Button
                onClick={handleBuyActions}
                disabled={purchasing}
                className="w-full bg-blue-600 hover:bg-blue-700"
              >
                {purchasing ? (
                  'Redirecting to checkout...'
                ) : (
                  <>
                    <TrendingUp className="w-4 h-4 mr-2" />
                    Buy 50 Actions
                  </>
                )}
              </Button>
            </div>

            {/* Info */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-xs text-amber-800">
                <strong>💡 Pro Tip:</strong> Subscribe to get 100 actions/month for $0.89 
                (less than 1¢ per action) for consistent automation needs.
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ActionsBalance;
